/**
 * branding.config.ts — env-var driven brand config.
 *
 * Reads VITE_* variables baked in at build time (Vite client) or from
 * process.env at runtime (Vercel serverless). Falls back to generic FirePCR
 * defaults. Set VITE_* vars in Vercel per project to white-label per tenant.
 * See .env.example for the full list.
 */
import type { BrandConfig } from './config.js'
import { brandDefaults as d } from './defaults.js'

// In Vite builds, import.meta.env is statically replaced at bundle time.
// In Vercel serverless, import.meta.env is undefined — fall back to process.env.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const e = ((import.meta as any).env ?? process.env) as Record<string, string | undefined>

export const brand: BrandConfig = {
  // ── Company Identity ────────────────────────────────────────────────────
  companyName:         e.VITE_ORG_NAME           || d.companyName,
  companyLegal:        e.VITE_ORG_LEGAL_NAME      || d.companyLegal,
  appName:             e.VITE_APP_NAME            || d.appName,
  appBrand:            e.VITE_APP_BRAND           || d.appBrand,
  domain:              e.VITE_ORG_DOMAIN          || d.domain,
  supportEmail:        e.VITE_SUPPORT_EMAIL       || d.supportEmail,
  notificationsEmail:  e.VITE_NOTIFICATIONS_EMAIL || d.notificationsEmail,

  // ── Logo & Assets ───────────────────────────────────────────────────────
  logoUrl: e.VITE_ORG_LOGO_URL || d.logoUrl,

  // ── NEMSIS / EMS Identifiers ────────────────────────────────────────────
  nemsisAgencyId:        e.VITE_NEMSIS_AGENCY_ID        || d.nemsisAgencyId,
  nemsisStateAgencyId:   e.VITE_NEMSIS_STATE_AGENCY_ID  || d.nemsisStateAgencyId,
  nemsisStateCode:       e.VITE_NEMSIS_STATE_CODE       || d.nemsisStateCode,
  nemsisLemsa:           e.VITE_NEMSIS_LEMSA            || d.nemsisLemsa,
  nemsisSoftware:        e.VITE_NEMSIS_SOFTWARE         || d.nemsisSoftware,
  nemsisSoftwareCreator: e.VITE_NEMSIS_SOFTWARE_CREATOR || d.nemsisSoftwareCreator,

  // ── Unit Naming Conventions ─────────────────────────────────────────────
  unitPrefixes: {
    ambulance: e.VITE_UNIT_PREFIX_AMBULANCE || d.unitPrefixes.ambulance,
    medUnit:   e.VITE_UNIT_PREFIX_MEDUNIT   || d.unitPrefixes.medUnit,
    rems:      e.VITE_UNIT_PREFIX_REMS      || d.unitPrefixes.rems,
    warehouse: e.VITE_UNIT_PREFIX_WAREHOUSE || d.unitPrefixes.warehouse,
    truck:     e.VITE_UNIT_PREFIX_TRUCK     || d.unitPrefixes.truck,
  },

  // ── AI Assistant ────────────────────────────────────────────────────────
  assistantName:    e.VITE_ASSISTANT_NAME    || d.assistantName,
  assistantEmoji:   e.VITE_ASSISTANT_EMOJI   || d.assistantEmoji,
  assistantContext: e.VITE_ASSISTANT_CONTEXT || d.assistantContext,

  // ── Legal / Consent Forms ───────────────────────────────────────────────
  consentEntity: e.VITE_CONSENT_ENTITY || d.consentEntity,
  hipaaEntity:   e.VITE_HIPAA_ENTITY   || d.hipaaEntity,

  // ── Scheduling / Ops ────────────────────────────────────────────────────
  schedulerContext: e.VITE_SCHEDULER_CONTEXT || d.schedulerContext,

  // ── Push Notifications ──────────────────────────────────────────────────
  vapidContact: e.VITE_VAPID_CONTACT || d.vapidContact,

  // ── Documents ───────────────────────────────────────────────────────────
  handbookUrl: e.VITE_ORG_HANDBOOK_URL || d.handbookUrl,
}
